import React, { FC, useEffect, useRef, useState } from 'react'
import { Button, LoaderSpinner, Modal } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import {
  cancelTerminalSaveSession,
  createTerminalSaveSession,
  getTerminalSaveSession,
  TERMINAL_SAVE_SESSION_STATUS,
} from '../../../../functions/payment-types/lspayTerminalCardSave'

const INITIAL_POLL_DELAY_MS = 5000
const POLLING_INTERVAL_MS = 1000
const MAX_POLL_ERRORS = 3
const MAX_POLL_DURATION_MS = 60000

interface TerminalCardSaveModalProps {
  merchantReference: string
  customerReference: string
  customerName: string
  pspTerminalId: string
  terminalLabel?: string
  pli: string
  saleId?: string
  createdBy?: string
  metadata?: Record<string, string>
  onSuccess: () => void
  onError: (message: string) => void
  onCancelled: () => void
}

export const TerminalCardSaveModal: FC<TerminalCardSaveModalProps> = ({
  merchantReference,
  customerReference,
  customerName,
  pspTerminalId,
  terminalLabel,
  pli,
  saleId,
  createdBy,
  metadata,
  onSuccess,
  onError,
  onCancelled,
}) => {
  const t = useT()
  const active = useRef(true)
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isCreating, setIsCreating] = useState(true)
  const [isCancelling, setIsCancelling] = useState(false)
  const saveSessionIdRef = useRef<string | null>(null)

  const title = t("Save card to {customerName}'s profile", {
    customerName,
    _context: 'Modal title when saving a card on file via terminal',
  })

  useEffect(() => {
    const startSession = async () => {
      try {
        const { saveSessionId } = await createTerminalSaveSession({
          merchantReference,
          customerReference,
          pspTerminalId,
          pli,
          saleId,
          createdBy,
          metadata,
        })

        if (!active.current) return
        saveSessionIdRef.current = saveSessionId
        setIsCreating(false)

        let errorCount = 0
        const pollStartTime = Date.now()

        const pollStatus = async () => {
          if (!active.current) return

          if (Date.now() - pollStartTime > MAX_POLL_DURATION_MS) {
            onError(
              t('Card save timed out. Please try again.', {
                _context:
                  'Error message when terminal card save polling exceeds max duration',
              })
            )
            return
          }

          try {
            const result = await getTerminalSaveSession({
              merchantReference,
              customerReference,
              saveSessionId,
              pli,
            })

            if (!active.current) return

            errorCount = 0
            const { status } = result

            if (status === TERMINAL_SAVE_SESSION_STATUS.SUCCEEDED) {
              onSuccess()
              return
            }

            if (status === TERMINAL_SAVE_SESSION_STATUS.FAILED) {
              onError(
                t('Card save failed', {
                  _context: 'Error message when terminal card save fails',
                })
              )
              return
            }

            if (status === TERMINAL_SAVE_SESSION_STATUS.CANCELLED) {
              onCancelled()
              return
            }

            // in_progress or requires_action — keep polling
            pollTimeoutRef.current = setTimeout(pollStatus, POLLING_INTERVAL_MS)
          } catch {
            errorCount++
            if (!active.current) return

            if (errorCount >= MAX_POLL_ERRORS) {
              onError(
                t('Unable to check card save status. Please try again.', {
                  _context:
                    'Error message when polling for terminal card save status fails repeatedly',
                })
              )
              return
            }

            pollTimeoutRef.current = setTimeout(pollStatus, POLLING_INTERVAL_MS)
          }
        }

        // Start polling after initial delay
        pollTimeoutRef.current = setTimeout(pollStatus, INITIAL_POLL_DELAY_MS)
      } catch {
        if (active.current) {
          onError(
            t(
              terminalLabel
                ? 'Failed to initiate card save on {deviceLabel}'
                : 'Failed to initiate card save on terminal',
              {
                deviceLabel: terminalLabel,
                _context:
                  'Error message when creating terminal card save session fails',
              }
            )
          )
        }
      }
    }
    startSession()

    return () => {
      active.current = false
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCancel = async () => {
    active.current = false
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)

    if (saveSessionIdRef.current) {
      setIsCancelling(true)
      try {
        await cancelTerminalSaveSession({
          merchantReference,
          customerReference,
          saveSessionId: saveSessionIdRef.current,
          pli,
        })
        onCancelled()
      } catch {
        // Cancel failed — session likely already succeeded or failed,
        // so fetch the final status to show the correct outcome
        active.current = true
        setIsCancelling(false)
        const result = await getTerminalSaveSession({
          merchantReference,
          customerReference,
          saveSessionId: saveSessionIdRef.current,
          pli,
        })
        const { status } = result
        if (status === TERMINAL_SAVE_SESSION_STATUS.SUCCEEDED) {
          onSuccess()
        } else if (status === TERMINAL_SAVE_SESSION_STATUS.FAILED) {
          onError(
            t('Card save failed', {
              _context: 'Error message when terminal card save fails',
            })
          )
        } else {
          onCancelled()
        }
      }
    } else {
      onCancelled()
    }
  }

  return (
    <Modal dismissable={false} focusTrapActive={true} size="small">
      <div className="vd-pa6">
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>{title}</div>
        <div className="vd-flex vd-flex--align-center vd-mt4">
          <div style={{ flexShrink: 0 }}>
            <LoaderSpinner small />
          </div>
          <span className="vd-text--secondary vd-ml2">
            {isCreating
              ? t(
                  terminalLabel
                    ? 'Request sent to {deviceLabel}'
                    : 'Request sent to terminal',
                  {
                    deviceLabel: terminalLabel,
                    _context:
                      'Status message while initiating card save on terminal',
                  }
                )
              : t(
                  terminalLabel
                    ? 'Waiting for customer to complete process on {deviceLabel}'
                    : 'Waiting for customer to complete process on terminal',
                  {
                    deviceLabel: terminalLabel,
                    _context:
                      'Status message while waiting for customer to save card on terminal',
                  }
                )}
          </span>
        </div>
        <div className="vd-flex vd-flex--justify-end vd-mt5">
          <Button variant="no" onClick={handleCancel} disabled={isCancelling}>
            {t('Cancel', {
              _context: 'Cancel button on terminal card save modal',
            })}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
